import React, { useState, useEffect } from "react";
import {
  Grid,
  IconButton,
  Typography,
  Card,
  CardContent,
  Divider,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "./customhours.css";

dayjs.extend(customParseFormat);

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const AddCustomHours = ({ setCustomHoursData ,intialData}) => {
  const [hours, setHours] = useState(
    daysOfWeek.reduce((acc, day) => {
      acc[day] = [{ start: null, end: null }];
      return acc;
    }, {})
  );

  const [errorMessages, setErrorMessages] = useState(
    daysOfWeek.reduce((acc, day) => {
      acc[day] = [""];
      return acc;
    }, {})
  );

  const [focusedField, setFocusedField] = useState(null);
  const [alwaysOpen, setAlwaysOpen] = useState(false);

  useEffect(() => {
    const timeSlots = Object.keys(hours).reduce((acc, day) => {
      const formattedDay = day.toUpperCase();
      acc[formattedDay] = hours[day]
        .filter((time) => time.start && time.end)
        .map((time) => ({
          start: time.start.format("hh:mm A"),
          end: time.end.format("hh:mm A"),
        }));
      return acc;
    }, {});

    setCustomHoursData(timeSlots);
  }, [hours, setCustomHoursData]);

  const handleTimeChange = (day, index, type, newValue) => {
    const updatedHours = hours[day].map((time, i) =>
      i === index ? { ...time, [type]: newValue ? dayjs(newValue) : null } : time
    );

    setHours({
      ...hours,
      [day]: updatedHours,
    });

    const startTime = updatedHours[index].start;
    const endTime = updatedHours[index].end;

    if (startTime && endTime && endTime.isBefore(startTime)) {
      setErrorMessages((prev) => ({
        ...prev,
        [day]: ["Start time should be less than end time."],
      }));
    } else {
      setErrorMessages((prev) => ({
        ...prev,
        [day]: [""],
      }));
    }
  };

  const handleDeleteHour = (day, index) => {
    const updatedHours = hours[day].filter((_, i) => i !== index);
    setHours({
      ...hours,
      [day]: updatedHours.length > 0 ? updatedHours : [{ start: null, end: null }],
    });
  };

  const handleAlwaysOpenChange = (event) => {
    const isChecked = event.target.checked;
    setAlwaysOpen(isChecked);

    if (isChecked) {
      const updatedHours = daysOfWeek.reduce((acc, day) => {
        acc[day] = [{
          start: dayjs().hour(0).minute(0),
          end: dayjs().hour(23).minute(59),
        }];
        return acc;
      }, {});
      setHours(updatedHours);
    } else {
      const resetHours = daysOfWeek.reduce((acc, day) => {
        acc[day] = [{ start: null, end: null }];
        return acc;
      }, {});
      setHours(resetHours);
    }
  };

  return (
    <Card className="custom-hours-card">
      <CardContent>
        <FormControlLabel className="alwaysopen-label"
          control={
            <Checkbox
              checked={alwaysOpen}
              onChange={handleAlwaysOpenChange}
              color="primary"
              className="always-open-checkbox"
            />
          }
          label="Always Open"
        />

        {daysOfWeek.map((day, dayIndex) => (
          <React.Fragment key={day}>
            {dayIndex > 0 && <Divider className="day-divider" />}
            <div className="day-row-container">
              <div className="day-info-container">
                <Typography variant="body1" className="customhourday">
                  {day}
                </Typography>
              </div>
              <div className="time-picker-container">
                {hours[day]?.map((time, index) => (
                  <Grid
                    container
                    spacing={0}
                    key={index}
                    className="time-picker-row"
                  >
                    <Grid item xs={5} className="time-picker-grid">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label="Start time"
                          open={focusedField === `${day}-${index}-start`}
                          onOpen={() => setFocusedField(`${day}-${index}-start`)}
                          onClose={() => setFocusedField(null)}
                          slotProps={{
                            textField: {
                              variant: "filled",
                              size: "small",
                               
                              inputProps: {
                                placeholder: "",
                                onFocus: () => setFocusedField(`${day}-${index}-start`),
                              },
                            },
                          }}
                          value={time.start}
                          onChange={(newValue) =>
                            handleTimeChange(day, index, "start", newValue)
                          }
                          ampm={true}
                          className="time-picker"
                          viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                          disabled={alwaysOpen}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={5} className="time-picker-grid">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label="End time"
                          open={focusedField === `${day}-${index}-end`}
                          onOpen={() => setFocusedField(`${day}-${index}-end`)}
                          onClose={() => setFocusedField(null)}
                          slotProps={{
                            textField: {
                              variant: "filled",
                              size: "small",
                              inputProps: {
                                placeholder: "",
                                onFocus: () => setFocusedField(`${day}-${index}-end`),
                              },
                            },
                          }}
                          value={time.end}
                          onChange={(newValue) =>
                            handleTimeChange(day, index, "end", newValue)
                          }
                          ampm={true}
                          className="time-picker"
                          viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                          disabled={alwaysOpen}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={1} className="delete-button-grid">
                    <IconButton
                        color="error"
                        onClick={() => handleDeleteHour(day, index)}
                        className={`delete-button ${alwaysOpen ? "disabled" : ""}`}
                        disabled={alwaysOpen}
                      >
                        <DeleteIcon className="delete-icon" />
                      </IconButton>

                    </Grid>
                    {errorMessages[day][0] && (
                      <Grid item xs={12}>
                        <FormHelperText error>{errorMessages[day][0]}</FormHelperText>
                      </Grid>
                    )}
                  </Grid>
                ))}
              </div>
            </div>
          </React.Fragment>
        ))}
      </CardContent>
    </Card>
  );
};

export default AddCustomHours;
